import { Popover } from 'antd';
import Spinner from 'common/spinner/components/spinner';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';
import { Icon } from 'ui';
import { planningBoardWorklistId, publishSteps } from '../../../../../utils/app-configs';
import { getWorklistDataByIdRequest } from '../../../../../worklist/containers/worklist-actions';
import { blockConfig, viewConfig } from '../../../config';
import { forwardPlanningPlanned } from '../../../containers/gantt-actions';
import Border from './block-border';
import BlockDetails from './block-detail-popover';
import Info from './block-info';
import Statuses from './block-statuses';
import Travel from './block-travel';

const BlockWrapper = styled.div`
  width: 100%;
  height: ${p => p.height || 60}px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0px 3px 5px 0px rgba(207, 207, 207, 1);
  position: relative;
  ${p => p.isAbsence && !p.isInactive && `
    background-color: ${blockConfig.absenceColor.light};
  `}
  ${p => p.isRouteOptimisation && `
    background-color: ${blockConfig.routeOptimisationColor.light};
  `}
  ${p => p.isInactive && `
    color: ${blockConfig.inactiveColor.main};
  `}

  ${p => p.isLoading && !p.isRouteOptimisation &&
    `opacity: 0.7;
  `}

  ${p =>
    p.arrows &&
    (p.arrows === blockConfig.arrows.left || p.arrows === blockConfig.arrows.both) &&
    css`
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
    `}

  ${p =>
    p.arrows &&
    (p.arrows === blockConfig.arrows.right || p.arrows === blockConfig.arrows.both) &&
    css`
      border-top-right-radius: 0px;
      border-bottom-right-radius: 0px;
    `}
`;

const GridWrapper = styled.div`
  @keyframes heartbeat {
    from {
      outline-color: transparent;
      outline-width: 0px;
    }
    to {
      outline-color: ${p => p.color} !important;
      outline-width: 4px;
    }
  }
  height: ${p => p.height}px;
  display: grid;
  grid-template-columns: ${p => p.columnSize};
  overflow: hidden;
  ${p => p.isAnimated && `
    outline: 3px solid ${p.color};
    border-radius: 4px;
    animation-name: heartbeat;
    animation-iteration-count: infinite;
    animation-timing-function: ease-in-out;
    animation-direction: alternate;
    animation-duration: 0.8s;
  `}
  ${p => (p.isPopoverVisible || p.isHighlighted) && `
    outline: 3px solid ${p.color};
    border-radius: 4px;
  `}
  ${p =>
    p.arrows &&
    (p.arrows === blockConfig.arrows.left || p.arrows === blockConfig.arrows.both) &&
    css`
      border-left: 12px solid transparent;
      &::before {
        content: '';
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-right: 5px solid ${p.color};
        position: absolute;
        left: 4px;
        top: calc(50% - 7px);
      }
    `}

  ${p =>
    p.arrows &&
    (p.arrows === blockConfig.arrows.right || p.arrows === blockConfig.arrows.both) &&
    css`
      border-right: 12px solid transparent;
      &::after {
        content: '';
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
        border-left: 5px solid ${p.color};
        position: absolute;
        right: 4px;
        top: calc(50% - 7px);
      }
    `}
`;

const GridDiv = styled.div`
  overflow: hidden;
  grid-column: ${p => p.column};
`;

const StyledIcon = styled(Icon)`
  padding: 5px;
  font-size: 16px;
  float: right;
`;

const Wrapper = styled.div`
  opacity: ${p => p.opacity};
  .details-popover {
    width: 660px !important;
  }
  .ant-popover-inner-content {
    padding: 10px;
  }
  .ant-popover-inner{
    padding: 0px;
  }
`;

const StyledSpinner = styled.div`
  position: absolute !important;
  top: ${p => p.isAbsenceOrReservation ? -5 : 0}px !important;
  left: 40% !important;
  height: ${p => p.isAbsenceOrReservation ? 20 : 40}px;
  div {
    height: inherit !important;
  }
`;

const SmallSpinner = styled.div`
  padding: 5px 3px 0 6px;
  height: 85px;
  display: inline-block;
  line-height: 16px;
  height: 100%;
  overflow: hidden;
`;

const getStatusColor = (block, type = 'colorMain') => {
  if (block?.showAs === blockConfig.showAs.disabled) {
    return blockConfig.inactiveColor.main;
  }
  if (block?.type === blockConfig.types.absence) {
    return blockConfig.absenceColor.main;
  }
  if (block?.type === blockConfig.types.placeholder) {
    return blockConfig.routeOptimisationColor.main;
  }
  if (Object.keys(block).length && block.alert && blockConfig.alert[block?.alert?.level]) {
    return blockConfig.alert[block?.alert?.level][type];
  }
  return blockConfig.alert.none[type];
};

const calculateBorderLeft = (block) => {
  if (block.type !== blockConfig.types.fieldService) {
    return '0%';
  }
  // eslint-disable-next-line
  const left = block.travelToWidth / block.dimensionsWidth * 100;
  return `${left}%`;
};

const calculateBorderWidth = (block, adjustedBlockWidth) => {
  if (block.type !== blockConfig.types.fieldService) {
    return '100%';
  }
  // eslint-disable-next-line
  return `${adjustedBlockWidth}px`;
};

/**
 *  Block component
 *
 * @param {data} object - data of block
 */
const Block = ({
  data: block,
  publishToSAP,
  markDateConfirmed,
  splitBlock,
  isDoingAction,
  isDraggingOver,
  deletePlanning,
  fetchBlockData,
  fetchBlockDataSuccess,
  updateBlockId,
  updateBlockOperationInfo,
  markDateUnconfirmed,
  restoreBackup,
  recalculateTravelTime,
  planningAndViewToOpen,
  isHighlighted,
}) => {
  const blockRef = useRef(null);
  const dispatch = useDispatch();
  const [columnSize, setColumnSize] = useState('0% 1fr 1fr 0%');
  const [statusesView, setStatusesView] = useState(blockConfig.statusesView.full);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [statusWidth, setStatusWidth] = useState();
  const [isBlockLoading, setIsBlockLoading] = useState(false);
  const [statusColor, setStatusColor] = useState();
  const [statusColorTravel, setStatusColorTravel] = useState();
  const [borderLeft, setBorderLeft] = useState();
  const [borderWidth, setBorderWidth] = useState();
  const [blockWidth, setBlockWidth] = useState();
  const [isAnimated, setIsAnimated] = useState(false);

  useEffect(() => {
    if (planningAndViewToOpen?.id) {
      if (planningAndViewToOpen.id === block.id) {
        setIsAnimated(true);
      } else {
        setIsAnimated(false);
      }
    } else {
      setIsAnimated(false);
    }
  }, [planningAndViewToOpen?.id]);

  useEffect(() => {
    setStatusColorTravel(getStatusColor(block, 'colorTravel'));
    setStatusColor(getStatusColor(block));
  }, [block?.type, block.isPreview, block?.alert]);

  const isRouteOptimisation = block?.type === blockConfig.types.placeholder;

  useEffect(() => {
    if (isRouteOptimisation) {
      setIsBlockLoading(false);
      if (
        block?.operationInfo?.processInfo?.currentProcess === 0 &&
        !block?.operationInfo?.isFailed
      ) {
        dispatch(forwardPlanningPlanned({
          laneId: block.laneId,
        }));
      }
      return;
    }

    if (block.operationInfo && Object.keys(block.operationInfo).length) {
      const statusInfo = block.operationInfo;
      if (
        statusInfo.errors.length || statusInfo.isStale || statusInfo.isTimedOut
      ) {
        if (block?.objectId) {
          dispatch(getWorklistDataByIdRequest({
            ids: [block.objectId],
            worklistId: planningBoardWorklistId,
            highlightIds: [block?.objectId, block?.id],
          }));
        }
        if (isBlockLoading) {
          setIsBlockLoading(false);
        }
      } else if (
        (
          statusInfo.status >= publishSteps.unknown.status &&
          statusInfo.status < publishSteps.done.status
        ) ||
        statusInfo.status === publishSteps.waitCycle.status
      ) {
        if (!isBlockLoading) {
          setIsBlockLoading(true);
        }
      } else if (
        isBlockLoading &&
        statusInfo.status === publishSteps.done.status
      ) {
        fetchBlockData({
          id: block.id,
          boardId: viewConfig.boardIdTypes.service,
        });
        if (isBlockLoading) {
          setIsBlockLoading(false);
        }
      }
    }
  }, [
    block?.operationInfo?.errors?.length,
    block?.operationInfo?.isStale,
    block?.operationInfo?.isTimedOut,
    block?.operationInfo?.status,
    block?.operationInfo?.processInfo?.currentProcess,
  ]);

  useEffect(() => {
    if (block?.newId && block?.newId !== block?.id) {
      updateBlockId({
        from: block.id,
        to: block.newId,
        laneId: block.laneId,
      });
      fetchBlockData({
        id: block.newId,
        boardId: viewConfig.boardIdTypes.service,
      });
    }
  }, [block?.newId]);

  useEffect(() => {
    if (blockRef && blockRef?.current) {
      setBlockWidth(blockRef?.current?.clientWidth);
    }
  }, [blockRef?.current?.clientWidth]);

  useEffect(() => {
    setIsPopoverVisible(isPopoverVisible && !isDraggingOver);
  }, [isDraggingOver]);

  useEffect(() => {
    if (blockWidth) {
      // const widthDataBlock = statusesRefSize.width + infoRefSize.width;
      const widthDataBlock = blockWidth;
      let travelToAdjustPx = 0;
      let travelFromAdjustPx = 0;
      // eslint-disable-next-line
      const travelToPct = block.travelToWidth / block.dimensionsWidth * 100;
      // eslint-disable-next-line
      const travelFromPct = block.travelFromWidth / block.dimensionsWidth * 100;

      if (block.arrows === 'left' || block.arrows === 'both') {
        travelToAdjustPx = 10;
      }
      if (block.arrows === 'right' || block.arrows === 'both') {
        travelFromAdjustPx = 6;
      }

      const travelTo = travelToAdjustPx ?
        `calc(${travelToPct}% - ${travelToAdjustPx}px)` :
        `${travelToPct}%`;
      const travelFrom = travelFromAdjustPx ?
        `calc(${travelFromPct}% - ${travelFromAdjustPx}px)` :
        `${travelFromPct}%`;

      const adjustedWidthDataBlock =
        // eslint-disable-next-line
        (widthDataBlock * (100 - travelToPct - travelFromPct) - travelToAdjustPx - travelFromAdjustPx) / 100;

      setBorderWidth(calculateBorderWidth(block, adjustedWidthDataBlock));
      setBorderLeft(calculateBorderLeft(block));

      let statusesWidth = 0;

      if (adjustedWidthDataBlock >= 300) {
        setColumnSize(`${travelTo} 2fr 1fr ${travelFrom}`);
        statusesWidth = adjustedWidthDataBlock / 3;
      } else if (adjustedWidthDataBlock < 300 && adjustedWidthDataBlock >= 200) {
        setColumnSize(`${travelTo} 2fr 1fr ${travelFrom}`);
        statusesWidth = adjustedWidthDataBlock / 3;
      } else if (adjustedWidthDataBlock < 200 && adjustedWidthDataBlock >= 100) {
        setColumnSize(`${travelTo} 2fr 1fr ${travelFrom}`);
        statusesWidth = adjustedWidthDataBlock / 3;
      } else if (adjustedWidthDataBlock < 100 && adjustedWidthDataBlock >= 50) {
        setColumnSize(`${travelTo} 1fr 2fr ${travelFrom}`);
        statusesWidth = (adjustedWidthDataBlock / 3) * 2;
      } else if (adjustedWidthDataBlock < 50) {
        setColumnSize(`${travelTo} 0fr 1fr ${travelFrom}`);
        statusesWidth = adjustedWidthDataBlock;
      } else {
        setColumnSize(`${travelTo} 1fr 1fr ${travelFrom}`);
        statusesWidth = adjustedWidthDataBlock / 2;
      }
      setStatusWidth(statusesWidth);
      if (statusesWidth < 100 && statusesWidth >= 55) {
        setStatusesView(blockConfig.statusesView.two);
      } else if (statusesWidth < 55) {
        setStatusesView(blockConfig.statusesView.one);
      } else {
        setStatusesView(blockConfig.statusesView.full);
      }
    }
  }, [blockWidth, block.duration, block.dimensionsWidth, block.isPreview]);

  const blockHeight = block?.type === blockConfig.types.fieldService ?
    blockConfig.height :
    blockConfig.smallHeight;

  // Soft delete the block, otherwise other blocks are also rerendered
  if (block?.isDeleted || block?.showAs === 'hidden') {
    return null;
  }

  return (
    <Wrapper
      color={statusColor}
      data-test-id='planning-block'
      onClick={e => e.stopPropagation()}
      isAbsenceOrReservation={block.type !== blockConfig.types.fieldService}
      opacity={block.outOfScope ? 0.5 : 1}
    >
      <Popover
        arrow={false}
        content={() =>
          isPopoverVisible && <BlockDetails
            block={block}
            setIsPopoverVisible={setIsPopoverVisible}
            publishToSAP={publishToSAP}
            splitBlock={splitBlock}
            markDateConfirmed={markDateConfirmed}
            isDoingAction={isDoingAction}
            deletePlanning={deletePlanning}
            isPopoverVisible={isPopoverVisible}
            fetchBlockDataSuccess={fetchBlockDataSuccess}
            updateBlockId={updateBlockId}
            updateBlockOperationInfo={updateBlockOperationInfo}
            isBlockLoading={isRouteOptimisation ? true : isBlockLoading}
            markDateUnconfirmed={markDateUnconfirmed}
            restoreBackup={restoreBackup}
            recalculateTravelTime={recalculateTravelTime}
          />
        }
        trigger='hover'
        mouseEnterDelay={0.3}
        mouseLeaveDelay={0.5}
        placement='bottomLeft'
        overlayClassName='details-popover'
        visible={isPopoverVisible && !block.isPreview}
        onVisibleChange={setIsPopoverVisible}
        overlayStyle={{ zIndex: 85 }}
        getPopupContainer={() => document.getElementsByClassName('fullscreen')[0]}
      >
        <BlockWrapper
          arrows={block.arrows}
          height={blockHeight}
          isAbsence={block?.type === blockConfig.types.absence}
          isRouteOptimisation={isRouteOptimisation}
          isInactive={block?.showAs === blockConfig.showAs.disabled}
          isLoading={isBlockLoading || block.isPreview}
          ref={blockRef}
          isPopoverVisible={isPopoverVisible}
        >
          <GridWrapper
            arrows={block.arrows}
            color={statusColor}
            columnSize={columnSize}
            height={blockHeight}
            isAnimated={isAnimated && !isPopoverVisible}
            isHighlighted={isHighlighted}
            isPopoverVisible={isPopoverVisible}
          >
            {block.type === blockConfig.types.fieldService ?
              <Travel color={statusColorTravel} isTo={true} /> :
              null
            }
            {block?.outOfScope ?
              <GridDiv column={3}>
                <StyledIcon type='circle-info' />
              </GridDiv> :
              <>
                <GridDiv column={2}>
                  <Info
                    loadingIconBefore={<SmallSpinner>
                      <Icon className='fa-solid fa-spinner fa-spin' size='s' />
                    </SmallSpinner>}
                    line1={block.title}
                    line2={block.line1}
                    line3={block.line2}
                  />
                </GridDiv>
                <GridDiv column={3}>
                  {!block.isPreview ?
                    <Statuses
                      isReservation={
                        block.type === blockConfig.types.reservation
                      }
                      isAbsence={
                        block.type === blockConfig.types.absence
                      }
                      isRouteOptimisation={isRouteOptimisation}
                      lock={block.lock}
                      isInactive={block?.showAs === blockConfig.showAs.disabled}
                      status={block.alert?.level}
                      tags={block.tags}
                      badges={block.badges}
                      isDraft={block.isDraft}
                      view={statusesView}
                      isAssignedPlanner={block?.isAssignedPlanner}
                      hasError={
                        block?.operationInfo?.errors?.length ||
                        block?.operationInfo?.isTimedOut ||
                        block?.operationInfo?.isStale
                      }
                      statusWidth={statusWidth}
                      isBlockLoading={isRouteOptimisation ? true : isBlockLoading}
                    /> :
                    null}
                </GridDiv>
              </>
            }
            {block.type === blockConfig.types.fieldService ?
              <Travel color={statusColorTravel} isTo={false} /> :
              null
            }
          </GridWrapper>
          <Border
            arrows={block.arrows}
            showAs={block.showAs}
            color={statusColor}
            isAbsenceOrReservation={block.type !== blockConfig.types.fieldService}
            left={borderLeft}
            width={borderWidth}
          />
          {/* If the block is loading and the width of the block is greater than
          40% of his width (= left property of the spinner)
          + the width of the spinner (40), show the spinner */}
          {(isBlockLoading || block.isPreview) &&
            // eslint-disable-next-line
            blockWidth > (blockWidth * 0.4 + 40)
            ? <StyledSpinner isAbsenceOrReservation={block.type !== blockConfig.types.fieldService}>
              <Spinner />
            </StyledSpinner>
            : null}
        </BlockWrapper>
      </Popover>
    </Wrapper>
  );
};

Block.propTypes = {
  data: PropTypes.object,
  publishToSAP: PropTypes.func,
  markDateConfirmed: PropTypes.func,
  isDoingAction: PropTypes.bool,
  isDraggingOver: PropTypes.bool,
  deletePlanning: PropTypes.func,
  fetchBlockData: PropTypes.func,
  fetchBlockDataSuccess: PropTypes.func,
  updateBlockId: PropTypes.func,
  updateBlockOperationInfo: PropTypes.func,
  splitBlock: PropTypes.func,
  markDateUnconfirmed: PropTypes.func,
  restoreBackup: PropTypes.func,
  recalculateTravelTime: PropTypes.func,
  planningAndViewToOpen: PropTypes.object,
  isHighlighted: PropTypes.bool,
};

export default Block;
